import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./Chatbot.module.css";
import { useNavigate } from "react-router-dom";
import { ChefHat, Sparkles, X, ArrowRight } from "lucide-react";

export default function Chatbot({ isOpen, onClose }) {
  const [messages, setMessages] = useState(() => [
    {
      text:
        "Hi! I’m your Recipe Specialist.\nTell me what you’re craving (pasta, chicken, healthy, breakfast…) and I’ll guide you to the best recipes.",
      sender: "bot",
      suggestions: [
        "Pasta ideas",
        "Chicken recipes",
        "Healthy meals",
        "Breakfast quick",
        "High protein",
        "Latest recipes",
      ],
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const quickPrompts = useMemo(
    () => ["Pasta 🍝", "Chicken 🍗", "Healthy 🥗", "Breakfast 🍳", "Dinner 🍽️"],
    []
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const normalize = (text) => text.trim().toLowerCase();

  const replyFor = (raw) => {
    const msg = normalize(raw);

    // Default fallback (keeps the convo friendly + helpful)
    let botMsg = {
      text:
        "I can help with recipes, meal ideas, and categories.\nTry one of these, or tell me an ingredient you have at home.",
      sender: "bot",
      suggestions: [
        "Pasta recipes 🍝",
        "Chicken recipes 🍗",
        "Healthy meals 🥗",
        "Breakfast ideas 🍳",
        "High protein 💪",
        "Latest recipes 🔥",
      ],
    };

    if (msg.includes("about") || msg.includes("who are you")) {
      botMsg = {
        text:
          "I’m your Recipe Specialist.\nI help you pick recipes fast, with smart suggestions based on what you like.\nAsk me: pasta, chicken, vegan, breakfast, high protein, or “latest”.",
        sender: "bot",
      };
    } else if (
      msg.includes("services") ||
      msg.includes("what can you do") ||
      msg.includes("help")
    ) {
      botMsg = {
        text:
          "Here’s what I can do:\n- Find recipes by category (pasta, chicken, vegan…)\n- Suggest healthy / high-protein meals\n- Send you directly to the right page\n\nWhat do you want to cook today?",
        sender: "bot",
        suggestions: ["Pasta", "Chicken", "Healthy", "Breakfast", "Latest"],
      };
    } else if (msg.includes("add recipe") || msg.includes("upload recipe")) {
      botMsg = {
        text: "Love it. Want to share your recipe? Tap below and add it.",
        sender: "bot",
        link: "/add-recipe",
        ctaLabel: "Add Recipe",
      };
    } else if (msg.includes("profile") || msg.includes("my account")) {
      botMsg = {
        text: "Sure — opening your profile.",
        sender: "bot",
        link: "/profile",
        ctaLabel: "Go to Profile",
      };
    } else if (msg.includes("weight loss")) {
      botMsg = {
        text: "Great goal. Here are lighter, healthy recipes.",
        sender: "bot",
        link: "/category/healthyRecipes",
        ctaLabel: "Healthy Recipes",
      };
    } else if (msg.includes("weight gain")) {
      botMsg = {
        text: "Got it — higher calorie, high-protein options coming up.",
        sender: "bot",
        link: "/category/highProteinRecipes",
        ctaLabel: "High Protein",
      };
    } else if (msg === "hello" || msg === "hi" || msg.includes("hello")) {
      botMsg = {
        text:
          "Hey! Tell me one thing:\n1) What you want to eat, or\n2) What ingredients you have.",
        sender: "bot",
        suggestions: ["Pasta", "Chicken", "Breakfast", "Healthy", "Dinner"],
      };
    } else if (msg.includes("family dinner")) {
      botMsg = {
        text: "Family dinner? Perfect. Try these crowd-pleasers.",
        sender: "bot",
        link: "/category/familyDinnerRecipes",
        ctaLabel: "Family Dinner",
      };
    } else if (msg.includes("slow cooker")) {
      botMsg = {
        text: "Slow cooker recipes are the best. Here you go.",
        sender: "bot",
        link: "/category/slowCookerRecipes",
        ctaLabel: "Slow Cooker",
      };
    } else if (msg.includes("latest") || msg.includes("trending")) {
      botMsg = {
        text: "Here are the latest trending recipes.",
        sender: "bot",
        link: "/category/latestRecipes",
        ctaLabel: "Latest Recipes",
      };
    } else if (msg.includes("plant") || msg.includes("vegan")) {
      botMsg = {
        text: "Plant-based coming right up.",
        sender: "bot",
        link: "/category/plantBasedRecipes",
        ctaLabel: "Plant-based",
      };
    } else if (msg.includes("date night")) {
      botMsg = {
        text: "Date night approved recipes — let’s go.",
        sender: "bot",
        link: "/category/dateneightRecipes",
        ctaLabel: "Date Night",
      };
    } else if (msg.includes("chicken")) {
      botMsg = {
        text: "Chicken recipes — quick and tasty.",
        sender: "bot",
        link: "/category/chickenRecipes",
        ctaLabel: "Chicken",
      };
    } else if (msg.includes("pasta")) {
      botMsg = {
        text: "Pasta time. Here are some delicious options.",
        sender: "bot",
        link: "/category/pastaRecipes",
        ctaLabel: "Pasta",
      };
    } else if (msg.includes("pineapple")) {
      botMsg = {
        text: "Pineapple recipes — sweet & tangy.",
        sender: "bot",
        link: "/category/pineappleRecipes",
        ctaLabel: "Pineapple",
      };
    } else if (msg.includes("potato")) {
      botMsg = {
        text: "Potato recipes — comfort food mode.",
        sender: "bot",
        link: "/category/potatoRecipes",
        ctaLabel: "Potato",
      };
    } else if (msg.includes("beef")) {
      botMsg = {
        text: "Beef recipes — hearty and satisfying.",
        sender: "bot",
        link: "/category/beefRecipes",
        ctaLabel: "Beef",
      };
    } else if (msg.includes("healthy")) {
      botMsg = {
        text: "Healthy recipes — balanced and tasty.",
        sender: "bot",
        link: "/category/healthyRecipes",
        ctaLabel: "Healthy",
      };
    } else if (msg.includes("veg") || msg.includes("vegetarian")) {
      botMsg = {
        text: "Vegetarian recipes — fresh and flavorful.",
        sender: "bot",
        link: "/category/vegetarianRecipes",
        ctaLabel: "Vegetarian",
      };
    } else if (msg.includes("protein")) {
      botMsg = {
        text: "High-protein picks — let’s build a strong plate.",
        sender: "bot",
        link: "/category/highProteinRecipes",
        ctaLabel: "High Protein",
      };
    } else if (msg.includes("breakfast")) {
      botMsg = {
        text: "Breakfast recipes — quick starts and classics.",
        sender: "bot",
        link: "/category/breakfastRecipes",
        ctaLabel: "Breakfast",
      };
    } else if (msg.includes("lunch")) {
      botMsg = {
        text: "Lunch recipes — easy and filling.",
        sender: "bot",
        link: "/category/lunchRecipes",
        ctaLabel: "Lunch",
      };
    } else if (msg.includes("dinner")) {
      botMsg = {
        text: "Dinner recipes — let’s make something special.",
        sender: "bot",
        link: "/category/dinnerRecipes",
        ctaLabel: "Dinner",
      };
    } else if (msg.includes("cookie")) {
      botMsg = {
        text: "Cookies? Say less.",
        sender: "bot",
        link: "/recipe/plantBasedRecipes/5",
        ctaLabel: "Open Cookies",
      };
    }

    return botMsg;
  };

  const sendMessage = (overrideText) => {
    const textToSend = overrideText ?? input;
    if (!textToSend.trim()) return;

    const userMsg = { text: textToSend, sender: "user" };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");

    setIsTyping(true);
    const botMsg = replyFor(textToSend);
    window.setTimeout(() => {
      setMessages((prev) => [...prev, botMsg]);
      setIsTyping(false);
    }, 450);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true">
      <div className={styles.chatbox}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.botAvatar} aria-hidden="true">
            <ChefHat className={styles.botAvatarIcon} />
          </div>
          <div className={styles.headerText}>
            <div className={styles.titleRow}>
              <span className={styles.title}>Recipe Specialist</span>
              <span className={styles.badge}>
                <Sparkles className={styles.badgeIcon} />
                Smart picks
              </span>
            </div>
            <span className={styles.subtitle}>Tell me a dish or ingredient.</span>
          </div>
        </div>

        <button className={styles.close} onClick={onClose} type="button" aria-label="Close chatbot">
          <X className={styles.closeIcon} />
        </button>
      </div>

      {/* Messages */}
      <div className={styles.messages}>
        {messages.map((msg, i) => (
          <div
            key={i}
            className={
              msg.sender === "user" ? styles.userMsg : styles.botMsg
            }
          >
            <div className={styles.msgText}>{msg.text}</div>

            {/* Link Button */}
            {msg.link && (
              <button
                onClick={() => navigate(msg.link)}
                className={styles.ctaBtn}
                type="button"
              >
                {msg.ctaLabel ?? "Open"} <ArrowRight className={styles.ctaIcon} />
              </button>
            )}

            {/* Suggestions */}
            {msg.suggestions && (
              <div className={styles.suggestions}>
                {msg.suggestions.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      sendMessage(s);
                    }}
                    className={styles.suggestionChip}
                    type="button"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

        {isTyping && (
          <div className={styles.botMsg}>
            <div className={styles.typing}>
              <span className={styles.dot}></span>
              <span className={styles.dot}></span>
              <span className={styles.dot}></span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className={styles.inputBox}>
        <div className={styles.quickRow}>
          {quickPrompts.map((p) => (
            <button
              key={p}
              type="button"
              className={styles.quickChip}
              onClick={() => sendMessage(p)}
            >
              {p}
            </button>
          ))}
        </div>

        <div className={styles.composer}>
          <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask: “chicken dinner”, “high protein”, “quick breakfast”…"
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
        />
          <button className={styles.sendBtn} onClick={() => sendMessage()} type="button">
            Send
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}